import { z } from "zod";
import { appLocale } from "../locale";
/**
 * Voice input for the trainer's composer (plan D30): the app records with
 * `MediaRecorder`, because the Web Speech API does not exist inside the
 * Android System WebView, and the server transcribes.
 *
 * Two surfaces share this file:
 *
 *   - `POST /me/transcribe` on `apps/api` — raw audio bytes in, `{ text }` out.
 *     The route checks the `Content-Type` against `TRANSCRIBE_MIME_TYPES` and
 *     the body against `TRANSCRIBE_MAX_BYTES`, and answers with
 *     `transcribeOutput`.
 *   - `transcribe(input)` — the RPC method `apps/ai` exposes on its
 *     `WorkerEntrypoint`, which the route calls over the `env.AI` service
 *     binding. The model key lives only in `apps/ai` (repo `D37`), so this is
 *     the one way `apps/api` reaches a model.
 */
/**
 * §12 of the plan's `api-contracts.md`, verbatim — the whole surface in one
 * block, so the route, the RPC and the client are read against one text:
 *
 * ```ts
 * // POST /me/transcribe   (bearer required)
 * // Body: the raw audio bytes (not JSON, not base64).
 * // Content-Type: one of audio/webm, audio/ogg, audio/mp4, audio/mpeg, audio/wav, audio/aac
 * //   (codec parameters allowed, e.g. "audio/webm;codecs=opus"; the server checks the base type).
 * // Limits: body ≤ 2 MiB (413 `transcribe.too_large`); unsupported type 415 `transcribe.unsupported_type`;
 * //   rate limit: identifier axis = account, 60 per rolling hour (429 via the existing limiter).
 * transcribeOutput = z.object({ text: z.string() });  // "" = no speech (not an error)
 *
 * // apps/ai RPC, called by apps/api over env.AI:
 * transcribe(input: {
 *   accountId: string;
 *   locale: "pt-BR" | "en-US";
 *   mimeType: string;          // base type, e.g. "audio/webm"
 *   audio: ArrayBuffer;        // Workers RPC carries it by structured clone
 *   vocabulary: string[];      // ≤200 exercise names the model should spell correctly
 * }) → { text: string }
 * // Gemini model = env TRANSCRIBE_MODEL ("gemini-3.1-flash-lite"), through the AI
 * // Gateway (repo D37), temperature 0, minimal thinking.
 * ```
 */
export const TRANSCRIBE_ROUTE = "/me/transcribe";
/**
 * The audio base types both sides accept. Codec parameters are allowed on the
 * wire (`audio/webm;codecs=opus`); what is checked, and what crosses the
 * binding, is the base type without them.
 */
export const TRANSCRIBE_MIME_TYPES = [
    "audio/webm",
    "audio/ogg",
    "audio/mp4",
    "audio/mpeg",
    "audio/wav",
    "audio/aac",
];
/**
 * The base type of a `Content-Type` header when it is one of
 * {@link TRANSCRIBE_MIME_TYPES}, else `null`.
 *
 * Parameters are dropped and the type is lowercased, because the header is
 * case-insensitive and Android's WebView records `audio/webm;codecs=opus` — a
 * strict string compare would refuse every real phone. The result is what
 * crosses the binding: the RPC accepts the base type only.
 */
export function transcribeBaseMimeType(contentType) {
    if (contentType === null || contentType === undefined) {
        return null;
    }
    const base = (contentType.split(";")[0] ?? "").trim().toLowerCase();
    return TRANSCRIBE_MIME_TYPES.includes(base)
        ? base
        : null;
}
/** 2 MiB — the route's body cap (413 `transcribe.too_large`) and the RPC's. */
export const TRANSCRIBE_MAX_BYTES = 2 * 1024 * 1024;
/** How many exercise names the model is asked to spell exactly. */
export const TRANSCRIBE_MAX_VOCABULARY = 200;
/** One vocabulary entry: an exercise name as the catalogue writes it. */
export const TRANSCRIBE_MAX_TERM_LENGTH = 120;
/**
 * What the route answers and what the RPC returns. `""` means no speech was
 * heard — it is NOT an error, and the client treats it as "nothing to insert".
 */
export const transcribeOutput = z.object({ text: z.string() });
/**
 * The audio, checked by `instanceof ArrayBuffer` and `byteLength` rather than
 * by anything Zod knows about binary data — there is no Zod type for an
 * `ArrayBuffer` that holds across every runtime this package is imported in.
 *
 * Workers RPC carries an `ArrayBuffer` by structured clone, so what arrives is
 * a buffer of the receiving isolate and `instanceof` holds there. A
 * `Uint8Array` is refused rather than unwrapped: a view over a larger buffer
 * would have shipped the WHOLE underlying buffer across the binding, and the
 * caller is the one that knows the exact length.
 */
const transcribeAudio = z
    .custom((value) => value instanceof ArrayBuffer, {
    message: "audio must be an ArrayBuffer",
})
    .refine((audio) => audio.byteLength >= 1, { message: "audio is empty" })
    .refine((audio) => audio.byteLength <= TRANSCRIBE_MAX_BYTES, {
    message: `audio exceeds ${TRANSCRIBE_MAX_BYTES} bytes`,
});
/**
 * The RPC input. `strictObject`, so a field renamed on the `apps/api` side of
 * the binding throws on the first call naming the field (`T-35`) instead of
 * arriving as `undefined`.
 */
export const transcribeRpcInput = z.strictObject({
    accountId: z.string().uuid(),
    audio: transcribeAudio,
    locale: appLocale,
    mimeType: z.enum(TRANSCRIBE_MIME_TYPES),
    vocabulary: z
        .array(z.string().trim().min(1).max(TRANSCRIBE_MAX_TERM_LENGTH))
        .max(TRANSCRIBE_MAX_VOCABULARY),
});
//# sourceMappingURL=transcribe.js.map